import React from 'react'
import { CheckCircle, Circle, ArrowRight } from 'lucide-react'
import { cn } from '../../lib/utils'

interface AuthProgressIndicatorProps {
  currentStep: 'userInfo' | 'otpVerification' | 'consentAcceptance'
}

export const AuthProgressIndicator: React.FC<AuthProgressIndicatorProps> = ({ currentStep }) => {
  const steps = [
    {
      id: 'userInfo',
      title: 'Identity',
      description: 'Secure Details'
    },
    {
      id: 'otpVerification',
      title: 'Verification',
      description: 'Email Verification'
    },
    {
      id: 'consentAcceptance',
      title: 'Consent',
      description: 'Platform Access'
    }
  ]

  const getStepStatus = (stepId: string) => {
    const stepOrder = ['userInfo', 'otpVerification', 'consentAcceptance']
    const currentIndex = stepOrder.indexOf(currentStep)
    const stepIndex = stepOrder.indexOf(stepId)

    if (stepIndex < currentIndex) return 'completed'
    if (stepIndex === currentIndex) return 'current'
    return 'upcoming'
  }

  return (
    <div className="w-full max-w-2xl mx-auto mb-8">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const status = getStepStatus(step.id)
          const isLast = index === steps.length - 1

          return (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300",
                    {
                      'bg-blue-600 border-blue-600 text-white': status === 'completed',
                      'bg-blue-100 border-blue-600 text-blue-600': status === 'current',
                      'bg-gray-100 border-gray-300 text-gray-400': status === 'upcoming'
                    }
                  )}
                >
                  {status === 'completed' ? (
                    <CheckCircle className="h-6 w-6" />
                  ) : (
                    <Circle className="h-6 w-6" />
                  )}
                </div>
                <div className="mt-2 text-center">
                  <div
                    className={cn(
                      "text-sm font-medium transition-colors duration-300",
                      {
                        'text-blue-600': status === 'completed' || status === 'current',
                        'text-gray-500': status === 'upcoming'
                      }
                    )}
                  >
                    {step.title}
                  </div>
                  <div
                    className={cn(
                      "text-xs transition-colors duration-300",
                      {
                        'text-blue-500': status === 'completed' || status === 'current',
                        'text-gray-400': status === 'upcoming'
                      }
                    )}
                  >
                    {step.description}
                  </div>
                </div>
              </div>
              
              {!isLast && (
                <div className="flex-1 px-4">
                  <div
                    className={cn(
                      "h-0.5 transition-colors duration-300",
                      {
                        'bg-blue-600': status === 'completed',
                        'bg-gray-300': status === 'current' || status === 'upcoming'
                      }
                    )}
                  />
                </div>
              )}
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}
